// src/hooks/useJobs.js
// ─────────────────────────────────────────────────────────────
//  실시간 공고 목록 + 검색 + 필터 훅
// ─────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef } from "react";
import { subscribeJobs, searchJobs, fetchJobs } from "../services/jobsService";

/**
 * 실시간 공고 목록 (Firestore onSnapshot)
 * @param {{ category, urgent }} filters
 */
export function useJobs(filters = {}) {
  const [jobs,    setJobs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    setLoading(true);
    const unsub = subscribeJobs(
      (data) => { setJobs(data); setLoading(false); },
      (err)  => { setError(err.message); setLoading(false); },
      filters,
    );
    return () => unsub();
  }, [filters.category, filters.urgent]);

  return { jobs, loading, error };
}

/**
 * 클라이언트 검색 + 카테고리 필터 조합
 */
export function useFilteredJobs() {
  const [category,    setCategory]    = useState("전체");
  const [keyword,     setKeyword]     = useState("");
  const [urgentOnly,  setUrgentOnly]  = useState(false);

  const { jobs, loading, error } = useJobs({
    category:  category !== "전체" ? category : undefined,
    urgent:    urgentOnly || undefined,
  });

  const filtered = searchJobs(jobs, keyword);

  return {
    jobs: filtered, loading, error,
    category, setCategory,
    keyword,  setKeyword,
    urgentOnly, setUrgentOnly,
  };
}

/**
 * 페이지네이션 (무한 스크롤용)
 */
export function useInfiniteJobs(filters = {}, pageSize = 20) {
  const [jobs,    setJobs]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const lastDocRef = useRef(null);

  const load = useCallback(async (reset = false) => {
    if (loading) return;
    setLoading(true);
    try {
      const result = await fetchJobs({
        ...filters,
        pageSize,
        lastDoc: reset ? null : lastDocRef.current,
      });
      setJobs(prev => reset ? result.jobs : [...prev, ...result.jobs]);
      lastDocRef.current = result.lastDoc;
      setHasMore(result.hasMore);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters.category, filters.urgent]);

  useEffect(() => { load(true); }, [filters.category, filters.urgent]);

  return { jobs, loading, hasMore, loadMore: () => load(false) };
}
